import type { Responsibility } from './types';

export function cx(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(' ');
}

export function humanize(value: string | null | undefined): string {
  if (!value) return 'Unknown';
  return value
    .replaceAll('_', ' ')
    .replaceAll('-', ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function formatDate(
  value: string | null | undefined,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (!value) return 'No date';
  const parsed = new Date(value.length === 10 ? `${value}T12:00:00Z` : value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat(
    'en-GB',
    options ?? { day: '2-digit', month: 'short', year: 'numeric' },
  ).format(parsed);
}

export function relativeDate(value: string | null | undefined): string {
  if (!value) return 'Unscheduled';
  const then = new Date(value).getTime();
  const days = Math.ceil((then - Date.now()) / 86_400_000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days === -1) return 'Yesterday';
  return days > 0 ? `In ${days} days` : `${Math.abs(days)} days overdue`;
}

export function responsibilityField<T>(
  item: Responsibility,
  camel: keyof Responsibility,
  snake: keyof Responsibility,
): T | undefined {
  return (item[camel] ?? item[snake]) as T | undefined;
}

export function statusTone(status: string | undefined): string {
  if (!status) return 'neutral';
  if (
    ['done', 'completed', 'current', 'verified', 'pass', 'cleared', 'ready', 'decided'].includes(
      status.toLowerCase(),
    )
  )
    return 'success';
  if (
    ['blocked', 'expired', 'conflicted', 'failed', 'unavailable', 'critical'].includes(
      status.toLowerCase(),
    )
  )
    return 'danger';
  if (
    ['waiting', 'stale', 'pending', 'caution', 'adviser_review', 'provisional'].includes(
      status.toLowerCase(),
    )
  )
    return 'warning';
  if (
    ['in_progress', 'next', 'applied', 'primary_verified', 'cross_reviewed'].includes(
      status.toLowerCase(),
    )
  )
    return 'info';
  return 'neutral';
}

export function isPast(value: string | null | undefined): boolean {
  return Boolean(value && new Date(value).getTime() < Date.now());
}

export const statusOrder = [
  'inbox',
  'backlog',
  'next',
  'in_progress',
  'waiting',
  'blocked',
  'done',
  'closed',
];
